
import { Fragment, useState, useEffect } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useCart } from '../../hooks/useCart';
import { formatPrice } from '../../utils/format';
import { redirectToShopifyCheckout } from '../../services/shopifyCheckout';

interface CartSlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartSlidePanel({ isOpen, onClose }: CartSlidePanelProps) {
  const { cartItems, incrementQuantity, decrementQuantity, removeItem, subtotal } = useCart();
  const [isVisible, setIsVisible] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setCheckoutError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    const error = await redirectToShopifyCheckout(cartItems);
    setCheckoutLoading(false);
    if (error) {
      setCheckoutError(error);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog className="relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-espresso/80 backdrop-blur-sm transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-screen max-w-md">
                  <div 
                    className="flex h-full flex-col shadow-2xl"
                    style={{
                      background: 'linear-gradient(180deg, #2D0A0F 0%, #4A1420 25%, #1A0F0A 75%, #2F1B0F 100%)'
                    }}
                  >
                    {/* Elegant Header */}
                    <div className="border-b border-brass/20 px-6 py-8">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="font-['Playfair_Display'] text-2xl text-parchment mb-1" style={{ fontFamily: '"Playfair Display", serif' }}>
                            Your Cart
                          </h2>
                          <p className="font-['Montserrat'] text-xs tracking-[0.2em] uppercase text-parchment/60">
                            Curated Selection
                          </p>
                        </div>
                        <button
                          type="button"
                          className="text-parchment/70 hover:text-brass transition-all duration-300 cursor-pointer group"
                          onClick={handleClose}
                        >
                          <div className="w-10 h-10 flex items-center justify-center rounded-full border border-brass/20 group-hover:border-brass/40 group-hover:bg-brass/10 transition-all duration-300">
                            <i className="ri-close-line text-xl" aria-hidden="true" />
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Cart Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-8">
                      {cartItems.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8">
                            <div className="relative">
                              <i className="ri-shopping-bag-3-line text-5xl text-brass/30" aria-hidden="true" />
                              <div className="absolute inset-0 animate-pulse">
                                <i className="ri-shopping-bag-3-line text-5xl text-brass/10" aria-hidden="true" />
                              </div>
                            </div>
                          </div>
                          <h3 className="font-['Playfair_Display'] text-xl text-parchment mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
                            Your Cart Awaits
                          </h3>
                          <p className="font-['Montserrat'] text-sm text-parchment/60 tracking-wide">
                            Discover our exquisite collection
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-8">
                          {cartItems.map((item, index) => {
                            const displayImage = item.selectedVariantImage || item.image;
                            const displayPrice = item.selectedVariantPrice || item.price;
                            const displayName = item.selectedVariantName && item.selectedVariantName !== 'Standard' 
                              ? item.selectedVariantName 
                              : null;
                            
                            return (
                              <div 
                                key={`${item.id}-${item.selectedVariantId || 'standard'}`} 
                                className="group animate-fadeInUp border-b border-brass/10 pb-8 last:border-b-0"
                                style={{ animationDelay: `${index * 100}ms` }}
                              >
                                <div className="flex gap-4">
                                  <div className="w-24 h-28 flex-shrink-0 overflow-hidden rounded-lg bg-gradient-to-b from-espresso/20 to-espresso/40 border border-brass/20">
                                    <img
                                      src={displayImage}
                                      alt={`${item.name}${displayName ? ` - ${displayName}` : ''}`}
                                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                                    />
                                  </div>

                                  <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                      <h3 className="font-['Playfair_Display'] text-lg text-parchment mb-1 group-hover:text-brass transition-colors duration-300" style={{ fontFamily: '"Playfair Display", serif' }}>
                                        {item.name}
                                      </h3>
                                      <p className="font-['Montserrat'] text-xs text-parchment/60 uppercase tracking-wider mb-2">
                                        {displayName || item.material}
                                      </p>
                                      <p className="font-['Montserrat'] text-base text-brass font-medium tracking-wide">
                                        {formatPrice(displayPrice)}
                                      </p>
                                    </div>

                                    {/* Quantity Controls */}
                                    <div className="flex items-center justify-between mt-4">
                                      <div className="flex items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={() => decrementQuantity(item.id, item.selectedVariantId || `${item.id}-standard`)}
                                          disabled={item.quantity <= 1}
                                          className="w-8 h-8 flex items-center justify-center rounded-full border border-brass/30 text-parchment/70 hover:text-brass hover:border-brass/60 hover:bg-brass/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer"
                                          aria-label="Decrease quantity"
                                        >
                                          <i className="ri-subtract-line text-sm" />
                                        </button>
                                        
                                        <span className="font-['Montserrat'] text-sm text-parchment min-w-[3rem] text-center tracking-wider">
                                          {item.quantity}
                                        </span>
                                        
                                        <button
                                          type="button"
                                          onClick={() => incrementQuantity(item.id, item.selectedVariantId || `${item.id}-standard`)}
                                          className="w-8 h-8 flex items-center justify-center rounded-full border border-brass/30 text-parchment/70 hover:text-brass hover:border-brass/60 hover:bg-brass/10 transition-all duration-300 cursor-pointer"
                                          aria-label="Increase quantity"
                                        >
                                          <i className="ri-add-line text-sm" />
                                        </button>
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => removeItem(item.id, item.selectedVariantId || `${item.id}-standard`)}
                                        className="font-['Montserrat'] text-xs text-parchment/60 hover:text-parchment uppercase tracking-wider cursor-pointer transition-colors duration-300"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Elegant Footer */}
                    {cartItems.length > 0 && (
                      <div className="border-t border-brass/20 px-6 py-8">
                        {/* Subtotal */}
                        <div className="flex justify-between items-center mb-6">
                          <p className="font-['Playfair_Display'] text-lg text-parchment" style={{ fontFamily: '"Playfair Display", serif' }}>
                            Subtotal
                          </p>
                          <p className="font-['Montserrat'] text-xl text-brass font-medium tracking-wide">
                            {formatPrice(subtotal)}
                          </p>
                        </div>
                        
                        <p className="font-['Montserrat'] text-xs text-parchment/60 text-center mb-8 tracking-wide">
                          Shipping and taxes calculated at checkout
                        </p>

                        {/* Checkout Button */}
                        <button 
                          type="button"
                          onClick={handleCheckout}
                          disabled={checkoutLoading}
                          className="w-full group relative overflow-hidden bg-gradient-to-r from-brass to-brass/90 hover:from-brass/90 hover:to-brass text-espresso font-['Montserrat'] font-semibold text-sm tracking-[0.1em] uppercase px-8 py-4 rounded-lg transition-all duration-500 shadow-lg hover:shadow-xl hover:shadow-brass/20 cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          <span className="relative z-10 flex items-center justify-center gap-2">
                            {checkoutLoading ? (
                              <>
                                <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="ri-lock-line text-base" aria-hidden="true" />
                                Secure Checkout
                              </>
                            )}
                          </span>
                          {!checkoutLoading && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                          )}
                        </button>

                        {/* Checkout Error */}
                        {checkoutError && (
                          <div className="mt-4 px-4 py-3 rounded-lg bg-red-900/30 border border-red-500/30">
                            <p className="font-['Montserrat'] text-xs text-red-300 text-center">
                              {checkoutError}
                            </p>
                          </div>
                        )}

                        {/* Secure Checkout Badge */}
                        <div className="mt-5 flex items-center justify-center gap-4 text-parchment/40">
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-shield-check-line text-sm" aria-hidden="true" />
                            </div>
                            <span className="font-['Montserrat'] text-[10px] tracking-wider uppercase">Secure</span>
                          </div>
                          <div className="w-px h-3 bg-parchment/20"></div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-visa-line text-sm" aria-hidden="true" />
                            </div>
                            <span className="font-['Montserrat'] text-[10px] tracking-wider uppercase">Cards</span>
                          </div>
                          <div className="w-px h-3 bg-parchment/20"></div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-truck-line text-sm" aria-hidden="true" />
                            </div>
                            <span className="font-['Montserrat'] text-[10px] tracking-wider uppercase">Insured</span>
                          </div>
                        </div>

                        {/* Continue Shopping */}
                        <div className="mt-6 text-center">
                          <p className="font-['Montserrat'] text-xs text-parchment/60 tracking-wider">
                            or{' '}
                            <button
                              type="button"
                              className="text-brass hover:text-parchment font-medium cursor-pointer transition-colors duration-300"
                              onClick={handleClose}
                            >
                              Continue Shopping
                            </button>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
